import { Rectangle } from 'pixi.js';
import type { CanvasTextStyleRenderer } from '../renderer/CanvasTextStyle';
import type { CanvasTextMetricItemAPI, CanvasWordElementMetrics } from './CanvasWordElementMetrics';
import type { CanvasWordMetrics } from './CanvasWordMetrics';
import type { CanvasTextLineMetrics } from './CanvasTextLineMetrics';

type PossibleChildren = CanvasTextLineMetrics | CanvasWordMetrics | CanvasWordElementMetrics;

/**
 * CanvasTextMetricList is our the base class for all text metric classes and contains a list of children. These will
 * either be lines, words or word elements. In this class we establish a contract for the API of these classes.
 */
export abstract class CanvasTextMetricList<T extends PossibleChildren> implements CanvasTextMetricItemAPI {
    #text: string = null;
    #ascent: number = null;
    #descent: number = null;
    #width: number = null;
    #height: number = null;
    #bounds: Rectangle = null;
    #ascentY: number = null;
    #descentY: number = null;
    #sourceWordReferenceIndices: number[] = null;
    #children: T[] = [];

    constructor(children?: T[]) {
        this.#children = children || [];
    }

    updateStyles(normalStyle: CanvasTextStyleRenderer, highlightStyle: CanvasTextStyleRenderer) {
        this.children.forEach(child => child.updateStyles(normalStyle, highlightStyle));
    }

    get children() {
        return this.#children;
    }

    removeChildrenFromEnd(count: number) {
        this.#children.splice(-count, count);
        this.clearCachedValues();
    }

    getChildrenAfterIndex(index: number): T[] {
        if (index === -1) {
            return [];
        }
        return this.children.slice(index + 1);
    }

    /**
     * Clear all cached values
     */
    clearCachedValues() {
        this.#text = null;
        this.#ascent = null;
        this.#descent = null;
        this.#width = null;
        this.#height = null;
        this.#bounds = null;
        this.#ascentY = null;
        this.#descentY = null;
        this.#sourceWordReferenceIndices = null;
    }

    get style(): CanvasTextStyleRenderer {
        if (this.#children.length === 0) {
            throw new Error('Cannot get text word style, no word elements exist');
        }
        return this.#children[0].style;
    }

    get width(): number {
        if (this.#width === null) {
            this.#width = this._getWidth();
        }
        return this.#width;
    }

    _getWidth(): number {
        return this.#children.reduce((acc, child) => acc + child.width, 0);
    }

    get height(): number {
        if (this.#height === null) {
            this.#height = this._getHeight();
        }
        return this.#height;
    }

    _getHeight(): number {
        return this.#children.reduce((max, child) => Math.max(max, child.height), 0);
    }

    get ascent(): number {
        if (this.#ascent === null) {
            this.#ascent = this.#getAscent();
        }
        return this.#ascent;
    }

    #getAscent(): number {
        return this.#children.reduce((max, child) => Math.max(max, child.ascent), 0);
    }

    get descent(): number {
        if (this.#descent === null) {
            this.#descent = this.#getDescent();
        }
        return this.#descent;
    }

    #getDescent(): number {
        return this.#children.reduce((max, child) => Math.max(max, child.descent), 0);
    }

    get fontAscent(): number {
        if (this.#children.length === 0) {
            return 0;
        }
        return this.#children[0].fontAscent;
    }

    get fontDescent(): number {
        if (this.#children.length === 0) {
            return 0;
        }
        return this.#children[0].fontDescent;
    }

    get bounds(): Rectangle {
        if (this.#bounds === null) {
            this.#bounds = this.#getBounds();
        }
        return this.#bounds;
    }

    #getBounds(): Rectangle {
        if (this.#children.length === 0) {
            return Rectangle.EMPTY;
        }
        const box = this.#children[0].bounds.clone();
        for (let i = 1; i < this.#children.length; i++) {
            const bounds = this.#children[i].bounds;
            if (bounds.width > 0) {
                box.enlarge(bounds);
            }
        }
        return box;
    }

    get text(): string {
        if (this.#text === null) {
            this.#text = this.#getText();
        }
        return this.#text;
    }

    #getText(): string {
        return this.#children.reduce((acc, child) => acc + child.text, '');
    }

    get baselineY(): number {
        if (this.#children.length === 0) {
            return 0;
        }
        return this.#children[0].baselineY;
    }

    get ascentY(): number {
        if (this.#ascentY === null) {
            this.#ascentY = this.#getAscentY();
        }
        return this.#ascentY;
    }

    #getAscentY(): number {
        return this.#children.reduce((min, child) => Math.min(min, child.ascentY), Infinity);
    }

    get descentY(): number {
        if (this.#descentY === null) {
            this.#descentY = this.#getDescentY();
        }
        return this.#descentY;
    }

    #getDescentY(): number {
        return this.#children.reduce((max, children) => Math.max(max, children.descentY), -Infinity);
    }

    /**
     * Returns the sourceWordReferenceIndices of the first and last word in this text block. This is useful for mapping the
     * text block back to the original text.
     */
    get sourceWordReferenceIndexRange() {
        const sourceWordReferenceIndices = this.sourceWordReferenceIndices;
        return [
            sourceWordReferenceIndices.at(0),
            sourceWordReferenceIndices.at(-1),
        ];
    }

    /**
     * Returns a reference indices to the words in the source text. We use this to map our word metrics back to the
     * original text.
     * */
    get sourceWordReferenceIndices(): number[] {
        if (this.#sourceWordReferenceIndices === null) {
            this.#sourceWordReferenceIndices = this._getSourceWordReferenceIndices();
        }
        return this.#sourceWordReferenceIndices;
    }

    abstract _getSourceWordReferenceIndices(): number[];

    get fontAscentY(): number {
        return this.#children[0].fontAscentY;
    }

    get fontDescentY(): number {
        return this.#children[0].fontDescentY;
    }

    get fontHeight(): number {
        return this.#children[0].fontHeight;
    }

    /**
     * Returns the global word element index of the first word element in this metric.
     */
    get globalWordElementIndex(): number {
        if (this.#children.length === 0) {
            return -1;
        }
        return this.#children[0].globalWordElementIndex;
    }

    setPosition(x: number, y: number) {
        let offsetX = x;
        this.children.forEach(child => {
            child.setPosition(offsetX, y);
            offsetX += child.width;
        });
    }
}
